import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import apolloClient from '../queries';
import {
  FetchSession, CreateSession, UpdateSession,
  FetchGiftCards, FetchOtherSiteId,
} from '../queries/index.graphql';
import { getValidationErrorMessages } from '../utils/error';
import { getSessionId, saveSessionId } from '../utils/localStorage';
import { useQueryParams, makeQueryStrings } from '../utils/queryParams';
import { makeGiftCardDetailInput } from '../utils/input';
import { getSiteId, getOtherSiteId } from '../utils/cookie';
import environments  from '../environments';
import { setErrorMessages } from './app';
import { TIMEOUT_ERROR_CODE } from '../constants';

export const createSession = async (sessionInput) => {
  const { data } = await apolloClient.mutate({
    mutation: CreateSession,
    variables: {
      input: sessionInput,
    }
  });

  return saveSessionId(data.createSession.id);
};

export const updateSession = (input, onSuccess, onFailed) => async (dispatch) => {
  const sessionId = getSessionId();
  const { data, errors } = await apolloClient.mutate({
    mutation: UpdateSession,
    variables: {
      id: sessionId,
      input: input,
    },
    errorPolicy: 'all',
  });

  if (errors && errors.length) {
    if (onFailed) {
      onFailed(errors);
    } else {
      const messages = getValidationErrorMessages(errors);
      dispatch(setErrorMessages(messages));
    }
    return;
  }

  return onSuccess && onSuccess(data.session);
};

export const fetchSession = async (onError) => {
  const sessionId = getSessionId();
  if (!sessionId) {
    onError && onError();
    return;
  }

  const { data } = await apolloClient.query({
    query: FetchSession,
    variables: { id: sessionId },
    fetchPolicy: 'no-cache',
  });

  return data.session;
};

export const useSession = (location, onError) => {
  const [session, setSession] = useState(undefined);

  const init = async () => {
    setSession(undefined);
    const session = await fetchSession(onError);
    setSession(session);
  };

  const { pathname } = location;

  useEffect(() => {
    init();
  }, [pathname]);

  return session;
};

const registerGiftCards = (giftCardDetailInput, onSuccess, onFailed) => async (dispatch) => {
  const sessionId = getSessionId();
  const { data, errors } = await apolloClient.query({
    query: FetchGiftCards,
    variables: {
      sessionID: sessionId,
      mode: 'create',
      giftCard: giftCardDetailInput,
    },
    fetchPolicy: 'no-cache',
  });

  if (errors && errors.length) {
    if (onFailed) {
      onFailed(errors);
    } else {
      const messages = getValidationErrorMessages(errors);
      dispatch(setErrorMessages(messages));
    }
    return;
  }

  return onSuccess && onSuccess(data.results);
};

const clearGiftCards = (onSuccess, onFailed) => async (dispatch) => {
  const sessionId = getSessionId();
  const { data, errors } = await apolloClient.query({
    query: FetchGiftCards,
    variables: {
      sessionID: sessionId,
      mode: 'delete',
      giftCard: [],
    },
    fetchPolicy: 'no-cache',
  });

  if (errors && errors.length) {
    if (onFailed) {
      onFailed(errors);
    } else {
      const messages = getValidationErrorMessages(errors);
      dispatch(setErrorMessages(messages));
    }
    return;
  }

  return onSuccess && onSuccess(data.results);
};

export const useGiftCards = (dispatch, initialGiftCardInfo, giftCardEnterFormRef) => {
  const query = useQueryParams();
  const history = useHistory();
  const { gc: isValidGiftCard } = query;

  const [enabledGiftCard, setEnabledGiftCard] = useState(false);
  const [giftCards, setGiftCards] = useState([]);
  const [isEnteredByUser, setIsEnteredByUser] = useState(false);

  const giftCardEnterUrl = `${environments.API_BASE_URI}/giftCard`;

  const clearGiftCardFlagFromUrl = () => {
    delete query.gc;
    const search = makeQueryStrings(query);
    history.replace({ search });
  };

  useEffect(() => {
    const initialGiftCards = initialGiftCardInfo && initialGiftCardInfo.details;
    if (!initialGiftCards) {
      return;
    }

    if (!isValidGiftCard) {
      dispatch(clearGiftCards());
      return;
    }

    setEnabledGiftCard(true);
    setGiftCards(initialGiftCards);
    clearGiftCardFlagFromUrl();
  }, [initialGiftCardInfo]);

  const onChangeGiftCardInputAmount = (giftCardKey, newInputAmount) => {
    const newState = giftCards.map((g) => {
      if (g.giftCardKey !== giftCardKey) {
        return g;
      }

      return {
        ...g,
        inputAmount: newInputAmount,
      };
    });

    setGiftCards(newState);
    if (!isEnteredByUser) {
      setIsEnteredByUser(true);
    }

    const giftCardDetailInput = makeGiftCardDetailInput(newState);
    dispatch(registerGiftCards(giftCardDetailInput));
  };

  const clearGiftCardInputAmount = () => {
    const newState = giftCards.map((g) => ({
      ...g,
      inputAmount: '0',
    }));

    setGiftCards(newState);
    dispatch(clearGiftCards());
  };

  const hasGiftCards = !!giftCards.length;

  const onChangeEnabledGiftCard = (value) => {
    setEnabledGiftCard(value);

    if (!hasGiftCards) {
      return;
    }

    if (value) {
      const giftCardDetailInput = makeGiftCardDetailInput(giftCards);
      dispatch(registerGiftCards(giftCardDetailInput));
    } else {
      dispatch(clearGiftCards());
    }
  };

  const onClickGiftLink = () => {
    const submitGiftCardForm = () => {
      giftCardEnterFormRef.current && giftCardEnterFormRef.current.submit();
    };

    if (hasGiftCards) {
      const alertMessage = '入力内容はクリアされますがよろしいですか？';
      const result = window.confirm(alertMessage);
      if (result) {
        dispatch(clearGiftCards(submitGiftCardForm));
      }
    } else {
      submitGiftCardForm();
    }
  };

  let giftCardInputTotalNumber = 0;
  giftCards.forEach((g) => {
    giftCardInputTotalNumber += Number(g.inputAmount);
  });

  return {
    enabledGiftCard, onChangeEnabledGiftCard, giftCards, giftCardInputTotalNumber, isEnteredByUser,
    onChangeGiftCardInputAmount, onClickGiftLink, giftCardEnterUrl, clearGiftCardInputAmount,
  };
};

export const useTimeout = () => {
  const query = useQueryParams();
  const { error: errorCode } = query;

  const isTimeoutError = errorCode === TIMEOUT_ERROR_CODE;

  return { isTimeoutError };
};

const fetchOtherSiteId = async () => {
  const sessionId = getSessionId();
  const siteId = getSiteId();
  const otherSiteId = getOtherSiteId();
  const otherSiteIdInput = {
    sessionId,
    siteId,
    otherSiteId,
  };

  const { data } = await apolloClient.query({
    query: FetchOtherSiteId,
    variables: {
      input: otherSiteIdInput,
    },
    fetchPolicy: 'no-cache',
  });

  return data.otherSiteId;
};

export const useOtherSiteId = () => {
  const [fetching, setFetching] = useState(true);
  const [otherSiteMemberId, setOtherSiteMemberId] = useState();
  const [otherSiteIdData, setOtherSiteIdData] = useState('');

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      setFetching(true);
      setOtherSiteIdData(undefined);
      const res = await fetchOtherSiteId();

      if (!isMounted) {
        return;
      }

      setOtherSiteIdData(res);

      const session = await fetchSession();

      if (!isMounted) {
        return;
      }

      if (!session) {
        setFetching(false);
        return;
      }
      const otherSiteMemberId = session.otherSiteId && session.otherSiteId.otherSiteMemberId;
      if (otherSiteMemberId) {
        setOtherSiteMemberId(otherSiteMemberId);
      }
      setFetching(false);
    };

    init();

    return () => {
      isMounted = false;
    };
  },[]);

  const showOtherSiteId = otherSiteIdData && otherSiteIdData.showFlag;
  const editOtherSiteId = otherSiteIdData && otherSiteIdData.editFlag;
  const requiredOtherSiteId = otherSiteIdData && otherSiteIdData.inputRequiredFlag;


  const { inputAttribute, minInputDigits, maxInputDigits } = otherSiteIdData || {};

  const inputAttributeDisplay = minInputDigits === maxInputDigits ?
    `${inputAttribute}${maxInputDigits}桁` : `${inputAttribute}${minInputDigits}~${maxInputDigits}桁`;

  return {
    otherSiteIdData, otherSiteMemberId, setOtherSiteMemberId,
    showOtherSiteId, editOtherSiteId, fetching, requiredOtherSiteId,
    inputAttributeDisplay,
  };
};
